/* eslint-disable no-use-before-define */
/* eslint-disable react-hooks/exhaustive-deps */
import Button from '@material-ui/core/Button';
import Checkbox from '@material-ui/core/Checkbox';
import Divider from '@material-ui/core/Divider';
import IconButton from '@material-ui/core/IconButton';
import InputAdornment from '@material-ui/core/InputAdornment';
import Paper from '@material-ui/core/Paper';
import { makeStyles } from '@material-ui/core/styles';
import TextField from '@material-ui/core/TextField';
import Typography from '@material-ui/core/Typography';
import { ArrowBack, ArrowForward, CheckRounded } from '@material-ui/icons';
import MaterialPhoneInput from '@orientaction/components/MaterialPhoneInput';
import {
  TURNSTILE_TOKEN_FIELD,
  TurnstileWidget,
  useTurnstile,
} from '@orientaction/components/Turnstile';
import { useResponseUser } from '@orientaction/hooks';
import { userService } from '@orientaction/services';
import { emailMatch, getFromLS, openCguPdf, openPrivacyPolicyPdf } from '@orientaction/utils';
import { isValidPhoneNumber } from 'libphonenumber-js';
import { FC, useContext, useState } from 'react';
import { Link, useHistory, useLocation } from 'react-router-dom';
import { UserProfilePhotoContext } from '../../../Context/UserProfilePhotoContext';
import config from '../../../services/config';
import { connectUser } from '../../../services/user.service';
import { publicIconPath } from '../../../utils/constants';
import { getAppleConnectUrl } from '../../../utils/sso';
import { claimShareResourcesAfterAuth } from '../../../utils/claimAuthorizedResource';
import { apiServiceNative } from '../../../utils/fetchNative';
import AccountConfirm from '../../Authentication/Commons/AccountConfirm';
import HelpModal from '../../Authentication/Commons/HelpModal';

interface IProps {
  testImage?: string;
  responses?: any;
  defaultEmail?: string;
  testId?: string;
  ebookResultId?: string;
  ebookIndex?: string;
  gameResultId?: string;
  authorizedGameId?: number | string;
  authorizedEbookId?: number | string;
  onGameLinked?: () => void;
  contentType?: 'test' | 'ebook' | 'game';
}

const PASSWORD_REGEX =
  /^(((?=.*[!@#$%^&*])(?=.*[A-Z])(?=.*[a-z]))|((?=.*\d)(?=.*[A-Z])(?=.*[a-z]))|((?=.*\d)(?=.*[!@#$%^&*])(?=.*[a-z]))|((?=.*\d)(?=.*[!@#$%^&*])(?=.*[A-Z]))).{8,}$/;

const deleteLead = async (email: string, tId?: string) => {
  if (!email || !tId) return;
  try {
    const emailParam = encodeURIComponent(email);
    const url = `/api/leads?filters[email][$eq]=${emailParam}&filters[test][id][$eq]=${tId}`;
    const res = await apiServiceNative.get(url);
    const items = res?.data || res;
    if (Array.isArray(items) && items.length > 0) {
      await Promise.all(items.map((item: any) => apiServiceNative.delete(`/api/leads/${item.id}`)));
    }
  } catch {
    // silent
  }
};

const InlineSignup: FC<IProps> = ({
  testImage,
  responses,
  defaultEmail = '',
  testId,
  ebookResultId,
  ebookIndex = '0',
  gameResultId,
  authorizedGameId,
  authorizedEbookId,
  onGameLinked,
  contentType = 'test',
}) => {
  const cls = useStyles();
  const history = useHistory();
  const { search } = useLocation();
  const { postResponseUserConnected } = useResponseUser();
  const { resetPhoto, updateUserProfilePhoto } = useContext<any>(UserProfilePhotoContext);
  const isEbook = contentType === 'ebook' && !!ebookResultId;
  const isGame = contentType === 'game' && !!gameResultId;

  const linkEbookResultToUser = async (userId: number) => {
    if (!ebookResultId) return;
    await apiServiceNative.put(`/api/update-ebook-result-user/${ebookResultId}`, {
      user_id: userId,
    });
  };

  const linkGameResultToUser = async (userId: number) => {
    if (!gameResultId) return;
    await apiServiceNative.put(`/api/update-result-game/${gameResultId}`, {
      user_id: userId,
      forBrevo: true,
    });
  };

  const continueEbookReading = () => {
    history.push(`/ebook/view/${ebookResultId}/${ebookIndex}`);
  };

  const continueGamePlaying = () => {
    if (onGameLinked) {
      onGameLinked();
      return;
    }
    history.push(`/game/play/${gameResultId}/0`);
  };

  const claimSharedResource = async (userId?: number, token?: string) => {
    if (!userId || (!isEbook && !isGame)) {
      return;
    }
    try {
      await claimShareResourcesAfterAuth({
        userId,
        token,
        gameId: isGame ? authorizedGameId : undefined,
        ebookId: isEbook ? authorizedEbookId : undefined,
      });
    } catch {
      // Attribution is best-effort and must not block login/signup.
    }
  };

  const [view, setView] = useState<'signup' | 'login'>('signup');
  const [step, setStep] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [resultId, setResultId] = useState<string | null>(null);
  const [showAccountConfirm, setShowAccountConfirm] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const [smsError, setSmsError] = useState('');

  const [loginForm, setLoginForm] = useState({ email: defaultEmail, password: '' });
  const [loginErrors, setLoginErrors] = useState<Record<string, string>>({});

  const [form, setForm] = useState({
    lastname: '',
    firstname: '',
    email: defaultEmail,
    password: '',
    confirmPassword: '',
    cgu: false,
  });
  const [step2, setStep2] = useState({ phone: '', postal_code: '' });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const turnstile = useTurnstile();

  const handleChange = (field: string) => (e: any) => {
    setForm({ ...form, [field]: e.target.value });
    setErrors({ ...errors, [field]: '' });
  };

  const validateStep1 = (): boolean => {
    const errs: Record<string, string> = {};
    if (!form.lastname.trim()) errs.lastname = 'Champ obligatoire';
    if (!form.firstname.trim()) errs.firstname = 'Champ obligatoire';
    if (!form.email.trim()) errs.email = 'Champ obligatoire';
    else if (!emailMatch.test(form.email)) errs.email = 'Veuillez saisir une adresse e-mail valide';
    if (!form.password) errs.password = 'Champ obligatoire';
    else if (!PASSWORD_REGEX.test(form.password))
      errs.password =
        'Au moins 8 caractères avec 3 des éléments : minuscules, majuscules, chiffres, spéciaux';
    if (!form.confirmPassword) errs.confirmPassword = 'Champ obligatoire';
    else if (form.password !== form.confirmPassword)
      errs.confirmPassword = "Le mot de passe n'est pas identique";
    if (!form.cgu) errs.cgu = "Veuillez accepter les conditions générales d'utilisation";
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleStep1Submit = async () => {
    if (!validateStep1()) return;

    const turnstileToken = turnstile.getTokenOrError();
    if (!turnstileToken) return;

    setIsLoading(true);
    try {
      await userService.verifyIfMailExist(form.email, turnstileToken);
      turnstile.reset();
      setStep(2);
    } catch {
      turnstile.reset();
      setErrors({
        ...errors,
        email:
          "Cette adresse e-mail est déjà utilisée. Veuillez vous connecter ou changer d'adresse.",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const validateStep2 = (): boolean => {
    const errs: Record<string, string> = {};
    if (!step2.phone || step2.phone.length <= 2) errs.phone = 'Champ obligatoire';
    else if (!isValidPhoneNumber(step2.phone)) errs.phone = 'Le format du numéro est invalide';
    if (!step2.postal_code) errs.postal_code = 'Champ obligatoire';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleStep2Submit = async () => {
    if (!validateStep2()) return;

    const turnstileToken = turnstile.getTokenOrError();
    if (!turnstileToken) return;

    setIsLoading(true);
    try {
      const from = new URLSearchParams(search).get('from');
      const registerData = await userService.registerUser({
        ...form,
        ...step2,
        language: 1,
        fromTest: from === 'test',
        company: 1,
        username: form.email,
        authorized_games: isGame ? [authorizedGameId] : undefined,
        authorized_ebooks: isEbook ? [authorizedEbookId] : undefined,
        [TURNSTILE_TOKEN_FIELD]: turnstileToken,
      });

      if (registerData?.data?.user?.confirmed === false) {
        setShowAccountConfirm(true);
        if (isEbook) {
          await linkEbookResultToUser(registerData?.data?.user.id);
          await apiServiceNative.post(
            '/api/users-permissions/user/set-post-confirmation-redirect',
            {
              userId: registerData?.data?.user.id,
              redirectTo: `/ebook/view/${ebookResultId}/${ebookIndex}`,
            }
          );
        } else if (isGame) {
          await linkGameResultToUser(registerData?.data?.user.id);
          await apiServiceNative.post(
            '/api/users-permissions/user/set-post-confirmation-redirect',
            {
              userId: registerData?.data?.user.id,
              redirectTo: `/game/play/${gameResultId}/0`,
            }
          );
        } else if (responses) {
          deleteLead(form.email || defaultEmail, testId);
          const dataToPost = {
            ...responses,
            users_permissions_users: [registerData?.data?.user.id],
          };
          const datareturn = await postResponseUserConnected(dataToPost, registerData?.data?.jwt);
          await apiServiceNative.post(
            '/api/users-permissions/user/set-post-confirmation-redirect',
            {
              userId: registerData?.data?.user.id,
              redirectTo: `/congrats/${datareturn.id}`,
            }
          );
        } else {
          history.push('/main');
        }
      }
    } catch {
      turnstile.reset();
      setErrors({ global: 'Une erreur est survenue. Veuillez réessayer.' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleRequestSms = async () => {
    if (!step2.phone) return;
    setSmsError('');
    try {
      const result = await apiServiceNative.post('/api/users-permissions/user/send-sms-code', {
        phone: step2.phone,
      });
      history.push({
        pathname: '/verify-phone',
        state: { phone: step2.phone, retryAfterSeconds: result?.retryAfterSeconds },
      });
    } catch {
      setSmsError("Une erreur est survenue lors de l'envoi du SMS. Veuillez réessayer.");
    }
  };

  const handleLoginSubmit = async () => {
    const errs: Record<string, string> = {};
    if (!loginForm.email.trim()) errs.email = 'Champ obligatoire';
    else if (!emailMatch.test(loginForm.email))
      errs.email = 'Veuillez saisir une adresse e-mail valide';
    if (!loginForm.password) errs.password = 'Champ obligatoire';
    setLoginErrors(errs);
    if (Object.keys(errs).length > 0) return;

    setIsLoading(true);
    setLoginErrors({});
    try {
      resetPhoto();
      const connectData = await connectUser({
        identifier: loginForm.email,
        password: loginForm.password,
      });
      if (connectData?.data?.jwt) {
        localStorage.setItem('userInfos', JSON.stringify(connectData?.data?.user));
        localStorage.setItem('userToken', connectData?.data?.jwt);
        updateUserProfilePhoto();
        deleteLead(loginForm.email || defaultEmail, testId);
        await claimSharedResource(connectData?.data?.user.id, connectData?.data?.jwt);

        if (isEbook) {
          await linkEbookResultToUser(connectData?.data?.user.id);
          continueEbookReading();
        } else if (isGame) {
          await linkGameResultToUser(connectData?.data?.user.id);
          continueGamePlaying();
        } else if (responses) {
          const dataToPost = {
            ...responses,
            users_permissions_users: [connectData?.data?.user.id],
          };
          const datareturn = await postResponseUserConnected(dataToPost, connectData?.data?.jwt);
          setResultId(datareturn.id);
        } else {
          history.push('/main');
        }
      }
    } catch (error: any) {
      if (error?.response?.data?.error?.name === 'ValidationError') {
        setLoginErrors({
          global: 'V\u00e9rifiez que vous avez rentr\u00e9 correctement vos identifiants.',
        });
      } else {
        setLoginErrors({ global: 'Une erreur est survenue. Veuillez r\u00e9essayer.' });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const renderPasswordToggle = () => (
    <IconButton
      aria-label={showPassword ? 'Masquer le mot de passe' : 'Afficher le mot de passe'}
      edge="end"
      onClick={() => setShowPassword(prev => !prev)}
      size="small">
      <img
        alt={showPassword ? 'eye-switch-on' : 'eye-switch-off'}
        src={`${publicIconPath}/${showPassword ? 'EyeOpened.svg' : 'EyeClosed.svg'}`}
        className={cls.eyeImg}
      />
    </IconButton>
  );

  const renderStepIndicator = () => (
    <div className={cls.stepIndicator}>
      {step === 1 && (
        <Typography className={cls.stepLink}>
          Vous avez d&eacute;j&agrave; un compte ?{' '}
          <span
            className={cls.stepLinkBold}
            style={{ cursor: 'pointer' }}
            onClick={() => {
              setView('login');
              setErrors({});
            }}
            role="button"
            tabIndex={0}
            onKeyDown={e => {
              if (e.key === 'Enter') {
                setView('login');
                setErrors({});
              }
            }}>
            Connectez-vous
          </span>
        </Typography>
      )}
      <div className={cls.steps}>
        <div className={cls.stepItem}>
          {step > 1 ? (
            <div className={cls.stepCheckDone}>&#10003;</div>
          ) : (
            <div className={cls.stepNumber}>1</div>
          )}
          <Typography className={step === 1 ? cls.stepLabelActive : cls.stepLabel}>
            Cr&eacute;ation compte
          </Typography>
        </div>
        <div className={cls.stepItem}>
          <div className={step === 2 ? cls.stepNumber : cls.stepNumberInactive}>2</div>
          <Typography className={step === 2 ? cls.stepLabelActive : cls.stepLabel}>
            Finaliser l&apos;inscription
          </Typography>
        </div>
      </div>
    </div>
  );

  if (showAccountConfirm) {
    return (
      <Paper className={cls.confirmCard}>
        <AccountConfirm
          title="Confirmation"
          email={form.email || defaultEmail}
          onRequestSms={step2.phone ? handleRequestSms : undefined}
          onNeedHelp={() => setHelpOpen(true)}
        />
        {smsError && <p className={cls.smsErrorText}>{smsError}</p>}
        <HelpModal
          open={helpOpen}
          onClose={() => setHelpOpen(false)}
          defaultEmail={form.email || defaultEmail}
        />
      </Paper>
    );
  }

  if (resultId) {
    return (
      <Paper className={cls.resultCard}>
        <div className={cls.resultIconWrapper}>
          <CheckRounded className={cls.resultIcon} />
        </div>
        <Typography className={cls.resultTitle}>R&eacute;sultats</Typography>
        <Typography className={cls.resultDescription}>
          Vous allez d&eacute;couvrir maintenant votre reconnaissance mon&eacute;taire
          privil&eacute;gi&eacute;e.
        </Typography>
        <Button
          className={cls.resultButton}
          onClick={() => history.push(`/congrats/${resultId}`)}
          endIcon={<ArrowForward />}>
          Voir les r&eacute;sultats
        </Button>
      </Paper>
    );
  }

  return (
    <Paper className={cls.card}>
      <div className={cls.layout}>
        <div className={cls.imageWrapper}>
          <img
            src="/images/MinorTest/test-share.png"
            alt={isEbook ? 'E-book' : 'Test'}
            className={cls.image}
          />
        </div>
        <div className={cls.content}>
          <div style={view !== 'login' ? { display: 'none' } : undefined}>
            <Typography className={cls.title}>
              {isEbook
                ? 'Poursuivre votre ebook'
                : isGame
                ? 'Poursuivre votre serious games'
                : 'Acc\u00e9dez \u00e0 vos r\u00e9sultats'}
            </Typography>
            <Typography className={cls.description}>
              {isEbook
                ? 'Créez votre compte pour enregistrer votre progression et poursuivre le ebook'
                : isGame
                ? 'Créez votre compte pour enregistrer votre progression et poursuivre le serious games'
                : 'Connectez-vous pour enregistrer votre progression et poursuivre le test.'}
            </Typography>

            <div className={cls.field}>
              <Typography className={cls.label}>Email</Typography>
              <TextField
                variant="outlined"
                fullWidth={true}
                size="small"
                type="email"
                value={loginForm.email}
                onChange={e => {
                  setLoginForm({ ...loginForm, email: e.target.value });
                  setLoginErrors({ ...loginErrors, email: '' });
                }}
                error={!!loginErrors.email}
                helperText={loginErrors.email}
                InputProps={{
                  className: cls.input,
                  endAdornment: (
                    <InputAdornment position="end">
                      <img src={`${publicIconPath}/Email.svg`} alt="Email" />
                    </InputAdornment>
                  ),
                }}
              />
            </div>

            <div className={cls.field}>
              <Typography className={cls.label}>Mot de passe</Typography>
              <TextField
                variant="outlined"
                fullWidth={true}
                size="small"
                type={showPassword ? 'text' : 'password'}
                value={loginForm.password}
                onChange={e => {
                  setLoginForm({ ...loginForm, password: e.target.value });
                  setLoginErrors({ ...loginErrors, password: '' });
                }}
                onKeyDown={e => {
                  if (e.key === 'Enter') handleLoginSubmit();
                }}
                error={!!loginErrors.password}
                helperText={loginErrors.password}
                InputProps={{
                  className: cls.input,
                  endAdornment: (
                    <InputAdornment position="end">{renderPasswordToggle()}</InputAdornment>
                  ),
                }}
              />
            </div>

            <Link to="/forgot-password" className={cls.forgotPassword}>
              Mot de passe oubli&eacute; ?
            </Link>

            {loginErrors.global && (
              <Typography className={cls.errorText}>{loginErrors.global}</Typography>
            )}

            <Button
              className={cls.submitButton}
              onClick={handleLoginSubmit}
              disabled={isLoading}
              fullWidth={true}>
              {isLoading ? 'Connexion...' : 'Connexion'}
            </Button>

            <div className={cls.dividerRow}>
              <Divider className={cls.divider} />
              <Typography className={cls.dividerText}>ou</Typography>
              <Divider className={cls.divider} />
            </div>

            <Button
              className={cls.socialButton}
              fullWidth={true}
              onClick={() => {
                window.location.href = `${config.api_url}/api/connect/google`;
              }}>
              <img src={`${publicIconPath}/Google.svg`} alt="Google" />
              <span className={cls.socialButtonText}>Connexion avec Google</span>
            </Button>
            <Button
              className={cls.socialButton}
              fullWidth={true}
              onClick={() => {
                window.location.href = `${config.api_url}/api/connect/facebook`;
              }}>
              <img src={`${publicIconPath}/Facebook.svg`} alt="Facebook" />
              <span className={cls.socialButtonText}>Connexion avec Facebook</span>
            </Button>
            <Button
              className={cls.socialButton}
              fullWidth={true}
              onClick={() => {
                window.location.href = getAppleConnectUrl();
              }}>
              <img src={`${publicIconPath}/Apple.svg`} alt="Apple" />
              <span className={cls.socialButtonText}>Connexion avec Apple</span>
            </Button>

            <Typography className={cls.bottomLink}>
              Pas encore de compte ?{' '}
              <span
                className={cls.stepLinkBold}
                style={{ cursor: 'pointer' }}
                onClick={() => {
                  setView('signup');
                  setLoginErrors({});
                }}
                role="button"
                tabIndex={0}
                onKeyDown={e => {
                  if (e.key === 'Enter') {
                    setView('signup');
                    setLoginErrors({});
                  }
                }}>
                Inscrivez-vous
              </span>
            </Typography>
          </div>

          <div style={view !== 'signup' || step !== 1 ? { display: 'none' } : undefined}>
            <Typography className={cls.title}>
              {isEbook
                ? 'Poursuivre votre ebook'
                : isGame
                ? 'Poursuivre votre serious games'
                : 'Acc\u00e9dez \u00e0 vos r\u00e9sultats'}
            </Typography>
            <Typography className={cls.description}>
              {isEbook
                ? 'Créez votre compte pour enregistrer votre progression et poursuivre le ebook'
                : isGame
                ? 'Créez votre compte pour enregistrer votre progression et poursuivre le serious games'
                : 'Connectez-vous pour enregistrer votre progression et poursuivre le test.'}
            </Typography>
            {renderStepIndicator()}

            <div className={cls.field}>
              <Typography className={cls.label}>Nom</Typography>
              <TextField
                variant="outlined"
                fullWidth={true}
                size="small"
                value={form.lastname}
                onChange={handleChange('lastname')}
                error={!!errors.lastname}
                helperText={errors.lastname}
                InputProps={{ className: cls.input }}
              />
            </div>
            <div className={cls.field}>
              <Typography className={cls.label}>Pr&eacute;nom</Typography>
              <TextField
                variant="outlined"
                fullWidth={true}
                size="small"
                value={form.firstname}
                onChange={handleChange('firstname')}
                error={!!errors.firstname}
                helperText={errors.firstname}
                InputProps={{ className: cls.input }}
              />
            </div>
            <div className={cls.field}>
              <Typography className={cls.label}>Email</Typography>
              <TextField
                variant="outlined"
                fullWidth={true}
                size="small"
                type="email"
                value={form.email}
                onChange={handleChange('email')}
                error={!!errors.email}
                helperText={errors.email}
                InputProps={{
                  className: cls.input,
                  endAdornment: (
                    <InputAdornment position="end">
                      <img src={`${publicIconPath}/Email.svg`} alt="Email" />
                    </InputAdornment>
                  ),
                }}
              />
              <Typography className={cls.helperText}>
                Vous allez recevoir un email pour confirmer votre inscription.
              </Typography>
            </div>
            <div className={cls.field}>
              <Typography className={cls.label}>Mot de passe</Typography>
              <TextField
                variant="outlined"
                fullWidth={true}
                size="small"
                type={showPassword ? 'text' : 'password'}
                value={form.password}
                onChange={handleChange('password')}
                error={!!errors.password}
                helperText={errors.password}
                InputProps={{
                  className: cls.input,
                  endAdornment: (
                    <InputAdornment position="end">{renderPasswordToggle()}</InputAdornment>
                  ),
                }}
              />
            </div>
            <div className={cls.field}>
              <Typography className={cls.label}>Confirmation du mot de passe</Typography>
              <TextField
                variant="outlined"
                fullWidth={true}
                size="small"
                type={showPassword ? 'text' : 'password'}
                value={form.confirmPassword}
                onChange={handleChange('confirmPassword')}
                error={!!errors.confirmPassword}
                helperText={errors.confirmPassword}
                InputProps={{
                  className: cls.input,
                  endAdornment: (
                    <InputAdornment position="end">{renderPasswordToggle()}</InputAdornment>
                  ),
                }}
              />
            </div>

            <div className={cls.cguRow}>
              <Checkbox
                checked={form.cgu}
                onChange={e => setForm({ ...form, cgu: e.target.checked })}
                className={cls.checkbox}
                color="primary"
              />
              <Typography className={cls.cguText}>
                J&apos;ai lu et j&apos;accepte les{' '}
                <Link
                  to="../../../../../orientaction-cgu.pdf"
                  target="_blank"
                  className={cls.cguLink}>
                  Conditions g&eacute;n&eacute;rales d&apos;utilisation
                </Link>{' '}
                et la{' '}
                <Link
                  to="../../../../../orientaction-pdc.pdf"
                  target="_blank"
                  className={cls.cguLink}>
                  Politique de confidentialit&eacute;
                </Link>
              </Typography>
            </div>
            {errors.cgu && <Typography className={cls.errorText}>{errors.cgu}</Typography>}

            {step === 1 && (
              <div className={cls.field}>
                <TurnstileWidget {...turnstile.widgetProps} error={turnstile.error} />
              </div>
            )}

            <Button
              className={cls.submitButton}
              onClick={handleStep1Submit}
              disabled={isLoading}
              fullWidth={true}>
              {isLoading ? 'Suivant...' : 'Suivant'}
            </Button>

            <div className={cls.dividerRow}>
              <Divider className={cls.divider} />
              <Typography className={cls.dividerText}>ou</Typography>
              <Divider className={cls.divider} />
            </div>

            <Button
              className={cls.socialButton}
              fullWidth={true}
              onClick={() => {
                window.location.href = `${config.api_url}/api/connect/google`;
              }}>
              <img src={`${publicIconPath}/Google.svg`} alt="Google" />
              <span className={cls.socialButtonText}>Inscription avec Google</span>
            </Button>
            <Button
              className={cls.socialButton}
              fullWidth={true}
              onClick={() => {
                window.location.href = `${config.api_url}/api/connect/facebook`;
              }}>
              <img src={`${publicIconPath}/Facebook.svg`} alt="Facebook" />
              <span className={cls.socialButtonText}>Inscription avec Facebook</span>
            </Button>
            <Button
              className={cls.socialButton}
              fullWidth={true}
              onClick={() => {
                window.location.href = getAppleConnectUrl();
              }}>
              <img src={`${publicIconPath}/Apple.svg`} alt="Apple" />
              <span className={cls.socialButtonText}>Inscription avec Apple</span>
            </Button>
          </div>

          <div style={view !== 'signup' || step !== 2 ? { display: 'none' } : undefined}>
            <Typography className={cls.title}>Finalisez votre inscription</Typography>
            <Typography className={cls.description}>
              Merci de nous indiquer votre num&eacute;ro de t&eacute;l&eacute;phone et votre code
              postal qui ne seront utilis&eacute;s que par les &eacute;quipes Orientaction.
            </Typography>
            {renderStepIndicator()}

            <div className={cls.field}>
              <Typography className={cls.label}>T&eacute;l&eacute;phone</Typography>
              <MaterialPhoneInput
                value={step2.phone as any}
                onChange={(val: any) => {
                  setStep2({ ...step2, phone: val || '' });
                  setErrors({ ...errors, phone: '' });
                }}
                error={!!errors.phone}
                style={{ border: '1px solid #CDCEDA', borderRadius: 8, height: 40 }}
              />
              {errors.phone && <Typography className={cls.errorText}>{errors.phone}</Typography>}
            </div>

            <div className={cls.field}>
              <Typography className={cls.label}>Code Postal</Typography>
              <TextField
                variant="outlined"
                fullWidth={true}
                size="small"
                value={step2.postal_code}
                onChange={e => {
                  setStep2({ ...step2, postal_code: e.target.value });
                  setErrors({ ...errors, postal_code: '' });
                }}
                error={!!errors.postal_code}
                helperText={errors.postal_code}
                InputProps={{ className: cls.input }}
              />
            </div>

            {errors.global && <Typography className={cls.errorText}>{errors.global}</Typography>}

            {step === 2 && (
              <div className={cls.field}>
                <TurnstileWidget {...turnstile.widgetProps} error={turnstile.error} />
              </div>
            )}

            <Button
              className={cls.submitButton}
              onClick={handleStep2Submit}
              disabled={isLoading}
              fullWidth={true}>
              {isLoading ? 'Cr\u00e9ation en cours...' : 'Cr\u00e9er mon compte'}
            </Button>

            <Button
              startIcon={<ArrowBack />}
              className={cls.backButton}
              onClick={() => {
                setStep(1);
                setErrors({});
                turnstile.reset();
              }}>
              Retour
            </Button>
          </div>
        </div>
      </div>
    </Paper>
  );
};

const useStyles = makeStyles(theme => ({
  card: {
    borderRadius: 0,
    padding: theme.spacing(5),
    backgroundColor: '#fff',
    boxShadow: '0 8px 32px rgba(35,41,111,0.10)',
    margin: '20px auto 0 auto',
    maxWidth: 900,
    width: '100%',
    boxSizing: 'border-box' as const,
    [theme.breakpoints.down('sm')]: {
      backgroundColor: 'transparent',
      boxShadow: 'none',
      padding: theme.spacing(2),
      marginTop: 0,
      margin: 0,
    },
  },
  layout: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 32,
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column' as const,
      gap: 20,
    },
  },
  imageWrapper: {
    flex: '0 0 40%',
    position: 'relative' as const,
    borderRadius: 4,
    overflow: 'hidden',
    '&::before': {
      content: '""',
      position: 'absolute',
      top: -6,
      left: -6,
      right: -6,
      bottom: -6,
      border: '2px solid #5B9BF5',
      borderRadius: 8,
      pointerEvents: 'none',
    },
    [theme.breakpoints.down('sm')]: {
      flex: '1 1 100%',
      maxHeight: 240,
      width: '100%',
    },
  },
  image: {
    width: '100%',
    height: '100%',
    objectFit: 'cover' as const,
    display: 'block',
    borderRadius: 4,
    minHeight: 280,
    [theme.breakpoints.down('sm')]: {
      minHeight: 180,
      maxHeight: 240,
    },
  },
  content: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column' as const,
  },
  title: {
    fontFamily: 'Poppins',
    fontWeight: 700,
    fontSize: 22,
    color: '#000A8D',
    marginBottom: 8,
  },
  description: {
    fontFamily: 'Poppins',
    fontSize: 14,
    lineHeight: 1.6,
    color: '#0E1247',
    marginBottom: 20,
  },
  stepIndicator: {
    marginBottom: 18,
  },
  stepLink: {
    fontFamily: 'Poppins',
    fontSize: 13,
    color: '#0E1247',
    marginBottom: 20,
    textAlign: 'center' as const,
  },
  stepLinkBold: {
    fontFamily: 'Poppins',
    fontWeight: 700,
    color: '#000A8C',
    textDecoration: 'underline',
  },
  steps: {
    display: 'flex',
    alignItems: 'center',
    gap: 16,
    marginTop: 8,
  },
  stepItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },
  stepNumber: {
    width: 32,
    height: 32,
    borderRadius: 6,
    backgroundColor: 'rgba(0,10,140,0.08)',
    color: '#000A8C',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontFamily: 'Poppins',
    fontWeight: 700,
    fontSize: 13,
  },
  stepNumberInactive: {
    width: 32,
    height: 32,
    borderRadius: 6,
    backgroundColor: '#E4E4E7',
    color: '#6B7280',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontFamily: 'Poppins',
    fontWeight: 700,
    fontSize: 13,
  },
  stepCheckDone: {
    width: 26,
    height: 26,
    borderRadius: 6,
    backgroundColor: '#E4E4E7',
    color: '#000A8C',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontFamily: 'Poppins',
    fontWeight: 700,
    fontSize: 13,
  },
  stepLabelActive: {
    fontFamily: 'Poppins',
    fontWeight: 700,
    fontSize: 13,
    color: '#000A8D',
  },
  stepLabel: {
    fontFamily: 'Poppins',
    fontWeight: 400,
    fontSize: 13,
    color: '#9CA3AF',
  },
  field: {
    marginBottom: 12,
  },
  label: {
    fontFamily: 'Poppins',
    fontWeight: 600,
    fontSize: 13,
    color: '#0E1247',
    marginBottom: 4,
  },
  input: {
    fontFamily: 'Poppins',
    fontSize: 14,
    borderRadius: 8,
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: '#CDCEDA',
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: '#000A8C',
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: '#000A8C',
      borderWidth: 1.5,
    },
  },
  eyeImg: {
    cursor: 'pointer',
    zIndex: 1,
    width: 16,
    height: 16,
  },
  helperText: {
    fontFamily: 'Poppins',
    fontSize: 12,
    color: '#6B7280',
    marginTop: 4,
  },
  cguRow: {
    display: 'flex',
    alignItems: 'flex-start',
    marginBottom: 4,
    marginLeft: -10,
  },
  checkbox: {
    '&.Mui-checked': {
      color: '#000A8C !important',
    },
  },
  cguText: {
    fontFamily: 'Poppins',
    fontSize: 12,
    color: '#0E1247',
    lineHeight: 1.5,
    paddingTop: 9,
  },
  cguLink: {
    color: '#000A8C',
    fontWeight: 600,
    textDecoration: 'none',
    '&:hover': {
      textDecoration: 'underline',
    },
  },
  cguLinkButton: {
    background: 'none',
    border: 'none',
    padding: 0,
    cursor: 'pointer',
    font: 'inherit',
  },
  forgotPassword: {
    fontFamily: 'Poppins',
    fontSize: 12,
    color: '#333333',
    textDecoration: 'underline',
    textAlign: 'right' as const,
    display: 'block',
    marginBottom: 12,
  },
  errorText: {
    fontFamily: 'Poppins',
    fontSize: 12,
    color: '#E63C3C',
    marginTop: 2,
    marginBottom: 4,
  },
  submitButton: {
    backgroundColor: '#000A8C',
    color: '#fff',
    borderRadius: 8,
    padding: '12px 24px',
    textTransform: 'none' as const,
    fontFamily: 'Poppins',
    fontSize: 16,
    fontWeight: 600,
    '&:hover': {
      backgroundColor: '#000A8C',
      opacity: 0.92,
    },
    '&:disabled': {
      backgroundColor: '#ccc !important',
      color: '#666 !important',
    },
  },
  dividerRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    margin: '16px 0',
  },
  divider: {
    flex: 1,
  },
  dividerText: {
    fontFamily: 'Poppins',
    fontSize: 13,
    color: '#9CA3AF',
  },
  socialButton: {
    width: '100%',
    height: 40,
    border: '1px solid #d8d8d8',
    borderRadius: 8,
    backgroundColor: '#fff',
    textTransform: 'none' as const,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    padding: '16px 24px',
    marginBottom: 8,
    '&:hover': {
      backgroundColor: '#f9f9f9',
    },
  },
  socialButtonText: {
    fontFamily: "'Open Sans', sans-serif",
    fontWeight: 600,
    fontSize: 14,
    color: '#000B33',
    letterSpacing: '0.28px',
    paddingLeft: 8,
  },
  backButton: {
    color: '#0E1247',
    textTransform: 'none' as const,
    fontFamily: 'Poppins',
    fontWeight: 600,
    fontSize: 14,
    marginTop: 8,
    justifyContent: 'flex-start',
    paddingLeft: 0,
  },
  bottomLink: {
    fontFamily: 'Poppins',
    fontSize: 13,
    color: '#0E1247',
    textAlign: 'center' as const,
    marginTop: 16,
  },
  resultCard: {
    borderRadius: 0,
    padding: theme.spacing(8, 5),
    backgroundColor: '#fff',
    boxShadow: '0 8px 32px rgba(35,41,111,0.10)',
    margin: '20px auto 0 auto',
    maxWidth: 900,
    width: '100%',
    boxSizing: 'border-box' as const,
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center' as const,
    minHeight: 420,
    [theme.breakpoints.down('sm')]: {
      backgroundColor: 'transparent',
      boxShadow: 'none',
      padding: theme.spacing(6, 2),
      marginTop: 0,
      margin: 0,
      minHeight: 360,
    },
  },
  resultIconWrapper: {
    width: 56,
    height: 56,
    borderRadius: 12,
    backgroundColor: '#E8E9F5',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 24,
  },
  resultIcon: {
    color: '#000A8C',
    fontSize: 32,
  },
  resultTitle: {
    fontFamily: 'Poppins',
    fontWeight: 700,
    fontSize: 24,
    color: '#000A8D',
    marginBottom: 12,
  },
  resultDescription: {
    fontFamily: 'Poppins',
    fontSize: 14,
    lineHeight: 1.6,
    color: '#0E1247',
    marginBottom: 28,
    maxWidth: 380,
  },
  resultButton: {
    backgroundColor: '#000A8C',
    color: '#fff',
    borderRadius: 8,
    padding: '12px 48px',
    textTransform: 'none' as const,
    fontFamily: 'Poppins',
    fontSize: 14,
    fontWeight: 600,
    minWidth: 320,
    '&:hover': {
      backgroundColor: '#000A8C',
      opacity: 0.92,
    },
    [theme.breakpoints.down('xs')]: {
      minWidth: '100%',
    },
  },
  confirmCard: {
    borderRadius: 0,
    padding: theme.spacing(6, 5),
    backgroundColor: '#fff',
    boxShadow: '0 8px 32px rgba(35,41,111,0.10)',
    margin: '20px auto 0 auto',
    maxWidth: 900,
    width: '100%',
    boxSizing: 'border-box' as const,
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    [theme.breakpoints.down('sm')]: {
      backgroundColor: 'transparent',
      boxShadow: 'none',
      padding: theme.spacing(4, 2),
      marginTop: 0,
      margin: 0,
    },
  },
  smsErrorText: {
    fontFamily: 'Poppins',
    fontSize: 13,
    color: '#E63C3C',
    textAlign: 'center' as const,
    marginTop: 12,
    marginBottom: 0,
  },
}));

export default InlineSignup;
